import { AxiosBaseQueryFunction } from 'api/axiosBaseQuery';

import { ChipMinimizerModel, CutoutModel, OptionalProduct, ProductDetails, TearOutModel } from '@common/models';
import { OptionalItemsResponse } from '@common/models/optional-items-response';
import { useFetchDependencies } from '@core/hooks/useFetchDependencies';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { CommonProductQueryParams } from '@ee-api/queryParams';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { createSelector } from '@reduxjs/toolkit';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useFetchOptionalItemsQuery } from './optionalItemsService';

const emptyArray = [] satisfies CutoutModel[] | OptionalProduct[] | ProductDetails[];

const filterOptionalProducts = (others: OptionalProduct[], chipMinimizer?: ChipMinimizerModel) =>
    others.filter((item) => item.productId !== chipMinimizer?.productId);

const selectOptionalProducts = createSelector(
    [(others?: CutoutModel[]) => others, (_others: CutoutModel[], chipMin?: OptionalProduct) => chipMin],
    (others, chipMin) => filterOptionalProducts(others ?? emptyArray, chipMin)
);

type useOptionalItemsReturn = TypedUseQueryHookResult<
    OptionalItemsResponse,
    CommonProductQueryParams,
    AxiosBaseQueryFunction
> & {
    cutouts: CutoutModel[];
    tearOuts: TearOutModel[];
    chipMinimizer?: ChipMinimizerModel;
    optionalProducts: OptionalProduct[];
    isFetchingOptionalItems: boolean;
    isErrorOptionalItems: boolean;
};

/**
 * @@deprecated use {@link OptionalItemKeys} instead
 * simple mapping for the json keys
 */
export enum Keys {
    cutOuts = 'cutouts',
    tearOuts = 'tear-outs',
    chipMinimizer = 'chip-minimizer',
    others = 'others',
}

export const OptionalItemKeys = Keys;

export const useOptionalItems = (): useOptionalItemsReturn => {
    const zipCode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { isFetchingDependencies } = useFetchDependencies();

    const { cutouts, tearOuts, chipMinimizer, optionalProducts, ...rest } = useFetchOptionalItemsQuery(
        !isFetchingDependencies && zipCode && selectedStore?.companyId && selectedColor?.qteGrpID
            ? {
                  params: {
                      zipCode,
                      storeId: selectedStore.companyId,
                      prodQteGrpID: selectedColor.qteGrpID,
                  },
              }
            : skipToken,
        {
            selectFromResult: (result) => {
                return result.isError
                    ? {
                          cutouts: emptyArray,
                          tearOuts: emptyArray,
                          chipMinimizer: undefined,
                          optionalProducts: emptyArray,
                          ...result,
                      }
                    : {
                          cutouts: result.data?.[Keys.cutOuts] ? result.data?.[Keys.cutOuts] : emptyArray,
                          tearOuts: result.data?.[Keys.tearOuts] ? result.data?.[Keys.tearOuts] : emptyArray,
                          chipMinimizer: result.data?.[Keys.chipMinimizer],
                          optionalProducts: result.data?.[Keys.others]
                              ? selectOptionalProducts(result.data?.[Keys.others], result.data?.[Keys.chipMinimizer])
                              : emptyArray,
                          ...result,
                      };
            },
        }
    );

    return {
        cutouts,
        tearOuts,
        chipMinimizer,
        optionalProducts,
        isFetchingOptionalItems: rest.isFetching,
        isErrorOptionalItems: rest.isError,
        ...rest,
    };
};
